/**
 * Notebook navigation / editor diagnostics.
 * Browser console: window.__INORA_NB_DEBUG__
 * Enable in prod: localStorage.setItem('inora:notebook-debug', '1')
 */

const STORAGE_KEY = 'inora:notebook-debug'

let seq = 0

function nextId() {
  seq += 1
  return seq
}

export function isNotebookDebugEnabled() {
  if (typeof window === 'undefined') return process.env.NODE_ENV === 'development'
  if (process.env.NEXT_PUBLIC_NOTEBOOK_DEBUG === '1') return true
  try {
    if (localStorage.getItem(STORAGE_KEY) === '1') return true
  } catch {
    /* ignore */
  }
  return process.env.NODE_ENV === 'development'
}

function ts() {
  return new Date().toISOString().slice(11, 23)
}

const STYLES = {
  nav: 'color:#38bdf8;font-weight:700',
  store: 'color:#a78bfa;font-weight:700',
  shell: 'color:#34d399;font-weight:700',
  editor: 'color:#fbbf24;font-weight:700',
  dom: 'color:#f472b6;font-weight:700',
  warn: 'color:#fb923c;font-weight:700',
  err: 'color:#f87171;font-weight:700',
  block: 'color:#94a3b8;font-weight:600',
}

/**
 * @param {'nav'|'store'|'shell'|'editor'|'dom'|'warn'|'err'|'block'} category
 */
export function nbLog(category, message, detail) {
  if (!isNotebookDebugEnabled()) return
  const style = STYLES[category] ?? STYLES.nav
  const prefix = `%c[Inora NB · ${category}]%c ${ts()} ${message}`
  if (detail !== undefined) {
    console.log(prefix, style, 'color:inherit', detail)
  } else {
    console.log(prefix, style, 'color:inherit')
  }
}

export function nbWarn(message, detail) {
  if (!isNotebookDebugEnabled()) return
  const prefix = `%c[Inora NB · WARN]%c ${ts()} ${message}`
  if (detail !== undefined) {
    console.warn(prefix, STYLES.warn, 'color:inherit', detail)
  } else {
    console.warn(prefix, STYLES.warn, 'color:inherit')
  }
}

export function nbError(message, detail) {
  if (!isNotebookDebugEnabled()) return
  const prefix = `%c[Inora NB · ERROR]%c ${ts()} ${message}`
  if (detail !== undefined) {
    console.error(prefix, STYLES.err, 'color:inherit', detail)
  } else {
    console.error(prefix, STYLES.err, 'color:inherit')
  }
}

/** Set from store.js after create() — avoids circular import */
let readStoreState = null

export function bindNotebookStoreReader(reader) {
  readStoreState = reader
}

/** Zustand slice useful for navigation bugs */
export function getStoreSnapshot() {
  if (!readStoreState) return { error: 'store reader not bound' }
  try {
    const s = readStoreState()
    const sectionCount = (s.notebook?.tabs ?? []).reduce(
      (n, t) => n + (t.sections?.length ?? 0),
      0,
    )
    return {
      sessionEpoch: s.sessionEpoch,
      contentReadyEpoch: s.contentReadyEpoch,
      hydrated: s.hydrated,
      notebookId: s.notebookId,
      activeTabId: s.activeTabId,
      sectionCount,
      isSaving: s.isSaving,
      saveStatus: s.saveStatus,
    }
  } catch (e) {
    return { error: String(e) }
  }
}

/** DOM scan — scoped to the active notebook root only (ignores Activity ghosts). */
export function scanNotebookDom(scopeRoot = null) {
  if (typeof document === 'undefined') {
    return { error: 'no document' }
  }

  const root =
    scopeRoot ??
    document.querySelector('.nb-root[data-nb-route-active="true"]') ??
    document.querySelector('.nb-root[data-nb-route-root]')

  if (!root) {
    return { hasNbRoot: false, proseMirrorCount: 0, tiptapFieldCount: 0 }
  }

  const proseMirrors = [...root.querySelectorAll('.ProseMirror')]
  const fields = [...root.querySelectorAll('[data-nb-tiptap-field]')]
  const richWrappers = [...root.querySelectorAll('.rich-block-wrapper')]
  const blockSlots = [...root.querySelectorAll('.nb-block-slot')]
  const blockSlotsWithSectionId = blockSlots.filter((el) =>
    el.hasAttribute('data-section-id'),
  )
  const richCanvases = [...root.querySelectorAll('.rich-text-canvas')]
  const routeEntryWrappers = [...root.querySelectorAll('[data-nb-route-entry]')]
  const routeMounts = [...root.querySelectorAll('.nb-route-mount')]
  const contentTracks = [...root.querySelectorAll('[data-nb-content-track]')]
  const editorCanvases = [...root.querySelectorAll('.nb-editor-canvas')]
  const allSectionIdNodes = [...root.querySelectorAll('[data-section-id]')]

  const blockSlotIds = blockSlotsWithSectionId.map((el) =>
    el.getAttribute('data-section-id'),
  )
  const blockSlotIdCounts = {}
  for (const id of blockSlotIds) {
    if (!id) continue
    blockSlotIdCounts[id] = (blockSlotIdCounts[id] ?? 0) + 1
  }
  const duplicateBlockSlotIds = Object.entries(blockSlotIdCounts)
    .filter(([, count]) => count > 1)
    .map(([id, count]) => ({ id, count }))

  const sectionIdNodeCounts = {}
  for (const el of allSectionIdNodes) {
    const id = el.getAttribute('data-section-id')
    if (!id) continue
    if (!sectionIdNodeCounts[id]) {
      sectionIdNodeCounts[id] = { count: 0, hosts: [] }
    }
    sectionIdNodeCounts[id].count += 1
    sectionIdNodeCounts[id].hosts.push({
      tag: el.tagName.toLowerCase(),
      className: el.className,
      isBlockSlot: el.classList.contains('nb-block-slot'),
      isBlockHost: el.classList.contains('nb-block-host'),
      isTabChild: el.classList.contains('tabs-child-block'),
    })
  }
  const duplicateSectionIdNodes = Object.entries(sectionIdNodeCounts)
    .filter(([, v]) => v.count > 1)
    .map(([id, v]) => ({ id, ...v }))

  const slotRects = blockSlots
    .map((el, index) => {
      const r = el.getBoundingClientRect()
      const sectionId = el.getAttribute('data-section-id')
      const blockType = el.getAttribute('data-block-type')
      return {
        index,
        sectionId,
        blockType,
        top: Math.round(r.top),
        left: Math.round(r.left),
        height: Math.round(r.height),
        width: Math.round(r.width),
      }
    })
    .filter((r) => r.height > 0 && r.width > 0)

  const blockSlotOverlaps = []
  for (let i = 0; i < slotRects.length; i++) {
    for (let j = i + 1; j < slotRects.length; j++) {
      const a = slotRects[i]
      const b = slotRects[j]
      const sameTop = Math.abs(a.top - b.top) < 6
      const sameLeft = Math.abs(a.left - b.left) < 6
      if (sameTop && sameLeft) {
        blockSlotOverlaps.push({
          aIndex: a.index,
          bIndex: b.index,
          aSectionId: a.sectionId,
          bSectionId: b.sectionId,
          aType: a.blockType,
          bType: b.blockType,
          top: a.top,
          left: a.left,
        })
      }
    }
  }

  const pmDetails = proseMirrors.map((pm, i) => {
    const field = pm.closest('[data-nb-tiptap-field]')
    const rect = pm.getBoundingClientRect()
    return {
      index: i,
      connected: pm.isConnected,
      owned: pm.dataset.editorOwned === 'true',
      inField: Boolean(field),
      fieldConnected: field?.isConnected ?? false,
      top: Math.round(rect.top),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      textPreview: (pm.textContent ?? '').slice(0, 40),
    }
  })

  const wrapperRects = richWrappers
    .map((el, i) => {
      const r = el.getBoundingClientRect()
      return {
        index: i,
        type: el.getAttribute('data-type'),
        top: Math.round(r.top),
        left: Math.round(r.left),
        height: Math.round(r.height),
        width: Math.round(r.width),
      }
    })
    .filter((r) => r.height > 0 && r.width > 0)

  const overlaps = []
  for (let i = 0; i < wrapperRects.length; i++) {
    for (let j = i + 1; j < wrapperRects.length; j++) {
      const a = wrapperRects[i]
      const b = wrapperRects[j]
      const sameTop = Math.abs(a.top - b.top) < 4
      const sameLeft = Math.abs(a.left - b.left) < 4
      if (sameTop && sameLeft) {
        overlaps.push({ a: i, b: j, aType: a.type, bType: b.type, top: a.top, left: a.left })
      }
    }
  }

  const orphanPm = pmDetails.filter((p) => !p.inField || !p.connected)
  const duplicateOwned = pmDetails.filter((p) => p.owned).length > fields.length

  return {
    hasNbRoot: Boolean(root),
    routeRootCount: document.querySelectorAll('.nb-root[data-nb-route-root]').length,
    routeEntryWrapperCount: routeEntryWrappers.length,
    routeMountCount: routeMounts.length,
    contentTrackCount: contentTracks.length,
    editorCanvasCount: editorCanvases.length,
    sectionIdNodeCount: allSectionIdNodes.length,
    uniqueBlockSlotSectionIds: Object.keys(blockSlotIdCounts).length,
    duplicateBlockSlotIds,
    duplicateSectionIdNodes,
    proseMirrorCount: proseMirrors.length,
    tiptapFieldCount: fields.length,
    richBlockWrapperCount: richWrappers.length,
    blockSlotCount: blockSlots.length,
    blockSlotWithSectionIdCount: blockSlotsWithSectionId.length,
    richCanvasCount: richCanvases.length,
    orphanProseMirror: orphanPm,
    richWrapperOverlaps: overlaps,
    blockSlotOverlaps,
    blockSlots: slotRects,
    possibleDuplicateEditors: duplicateOwned,
    proseMirrors: pmDetails,
    richWrappers: wrapperRects,
  }
}

export function snapshot(label, extra) {
  if (!isNotebookDebugEnabled()) return
  const dom = typeof document !== 'undefined' ? scanNotebookDom() : null
  const store = getStoreSnapshot()
  const payload = { label, store, dom, ...extra }
  nbLog('dom', `SNAPSHOT · ${label}`, payload)

  if (dom?.orphanProseMirror?.length > 0) {
    nbWarn('Orphan / disconnected ProseMirror nodes detected', dom.orphanProseMirror)
  }
  if (dom?.richWrapperOverlaps?.length > 0) {
    nbWarn('Rich block wrappers share same position (check for ghost mount)', dom.richWrapperOverlaps)
  }
  if (dom?.blockSlotOverlaps?.length > 0) {
    nbWarn('Block slots share same position (duplicate mount or bad layout)', dom.blockSlotOverlaps)
  }
  if (dom?.duplicateBlockSlotIds?.length > 0) {
    nbWarn('Duplicate .nb-block-slot[data-section-id] inside active root', dom.duplicateBlockSlotIds)
  }
  if (dom?.routeMountCount > 1) {
    nbWarn('Multiple .nb-route-mount inside active root (SPA subtree preserved)', {
      routeMountCount: dom.routeMountCount,
      routeEntryWrapperCount: dom.routeEntryWrapperCount,
    })
  }
  if (dom?.editorCanvasCount > 1) {
    nbWarn('Multiple .nb-editor-canvas inside active root', {
      editorCanvasCount: dom.editorCanvasCount,
    })
  }
  const collapsedSlots = (dom?.blockSlots ?? []).filter((s) => s.height > 0 && s.height <= 14)
  if (collapsedSlots.length > 0) {
    nbWarn('Collapsed block slots (content likely overflowing)', collapsedSlots)
  }
  if (dom?.proseMirrorCount > dom?.tiptapFieldCount + 2) {
    nbWarn('More ProseMirror nodes than tiptap fields', {
      pm: dom.proseMirrorCount,
      fields: dom.tiptapFieldCount,
    })
  }

  return payload
}

export function logMount(component, props) {
  const id = nextId()
  nbLog('nav', `MOUNT #${id} · ${component}`, props)
  return () => {
    nbLog('nav', `UNMOUNT #${id} · ${component}`, props)
    snapshot(`after-unmount-${component}`)
  }
}

export function installNotebookDebugGlobal() {
  if (typeof window === 'undefined') return
  if (window.__INORA_NB_DEBUG__) return

  const api = {
    enable: () => {
      try {
        localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        /* ignore */
      }
      console.log('[Inora NB] Debug enabled — reload page')
    },
    disable: () => {
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {
        /* ignore */
      }
      console.log('[Inora NB] Debug disabled — reload page')
    },
    snapshot,
    scan: scanNotebookDom,
    store: getStoreSnapshot,
    duplicateProbe: (label = 'duplicate-probe') => {
      const dom = scanNotebookDom()
      console.log(`[Inora NB · duplicate] ${label}`, dom)
      return dom
    },
    help: () => {
      console.log(`
[Inora Notebook Debug]
  __INORA_NB_DEBUG__.snapshot('label')  — store + DOM report
  __INORA_NB_DEBUG__.scan()             — DOM only
  __INORA_NB_DEBUG__.duplicateProbe('label') — duplicate section / route mount scan
  __INORA_NB_DEBUG__.enable()           — persist logs (reload)
  __INORA_NB_DEBUG__.disable()          — turn off (reload)

Filter console by: Inora NB
      `)
    },
  }

  window.__INORA_NB_DEBUG__ = api
  nbLog('nav', 'Debug installed → window.__INORA_NB_DEBUG__.help()')
}
